import { useState } from "react";
import { useTranslation } from "react-i18next";
import { renameTerm } from "../../../api/content/curationApi";
import type { Review } from "../../../api/content/reviewDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";

/**
 * A curator's rename of a term's IRI on the head shown (v1's TBox editing): one commit
 * that rewrites it in every module and in the change set's own items. Checks and the
 * conflict check run again on the new head.
 */
export default function RenameTerm({ review, term, onChanged }: { review: Review; term: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const submission = useSubmission();
  const [open, setOpen] = useState(false);
  const [iri, setIri] = useState(term);
  if (!open) {
    return (
      <AppButton
        size="sm"
        variant="ghost"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t("reviews.curation.rename.open")}
      </AppButton>
    );
  }
  const busy = submission.state.status === "submitting";
  return (
    <form
      className="space-y-1"
      onSubmit={(event) => {
        event.preventDefault();
        submission.submit(async () => {
          await renameTerm(review.changeSet.changeSetId, { expectedHead: review.head, term, newIri: iri.trim() });
          setOpen(false);
          onChanged();
        });
      }}
    >
      <Field label={t("reviews.curation.rename.label")} hint={t("reviews.curation.rename.explain")} className="text-xs">
        {(control) => (
          <div className="flex flex-wrap gap-2">
            <input
              {...control}
              className={`${FORM_COMPACT_CONTROL_CLASS} min-w-0 flex-1 font-mono`}
              value={iri}
              onChange={(event) => {
                setIri(event.target.value);
              }}
            />
            <AppButton size="sm" type="submit" disabled={busy || iri.trim() === "" || iri.trim() === term}>
              {t("reviews.curation.rename.send")}
            </AppButton>
            <AppButton
              size="sm"
              variant="ghost"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t("common.cancel")}
            </AppButton>
          </div>
        )}
      </Field>
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} compact={true} /> : null}
    </form>
  );
}
